import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useConfirm } from '../../provider/confirm.provider';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import {
  FiUpload,
  FiFile,
  FiCheckCircle,
  FiAlertCircle,
  FiX,
  FiDownload,
  FiRefreshCw,
  FiArrowRight,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiPlay,
  FiTrash2,
  FiArrowLeft
} from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../provider/auth.provider';
import { DESIGN_ACTIONS, DESIGN_RESOURCE } from './design_constants';

// Designing bulk site import — the same Upload / History screen as the INC
// import (pages/inc/upload.tsx), pointed at /designing/sites/*. The field list
// shown for mapping is `required_fields` of the analyze response.

interface ValidationError {
  row_number: number;
  column: string;
  value: any;
  error_type: string;
  message: string;
}

interface RequiredField {
  field_name: string;
  display_name: string;
  required: boolean;
  type: string;
  example?: string;
  description?: string;
}

interface AnalysisResult {
  upload_id: number;
  file_name: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  available_columns: string[];
  sheet_columns: string[];
  required_fields: RequiredField[];
  auto_mapped_fields: Record<string, string>;
  validation_errors: ValidationError[];
  suggestions: string[];
  incomplete_data_rows: number[];
  sample_data: any[];
}

// The server sends each failed row under both namings (row / row_number,
// message / error); this screen reads `row` and `message`, as the INC one does.
interface FailedRow {
  row: number;
  row_number?: number;
  message: string;
  error?: string;
  data?: Record<string, any>;
}

interface ProcessResult {
  upload_id: number;
  total_rows: number;
  success_count: number;
  failed_count: number;
  processed_rows: number;
  failed_rows: FailedRow[];
  status: string;
}

interface UploadHistoryItem {
  id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  total_rows: number;
  valid_rows?: number;
  invalid_rows?: number;
  processed_rows?: number;
  analysis_data?: object;
  field_mapping?: Record<string, string>;
  error_details?: object;
  status: string;
  uploaded_by: number;
  processed_at?: string;
  created_at?: string;
  updated_at?: string;
  uploader?: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
  };
}

interface HistoryPagination {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-xs border rounded text-left flex items-center justify-between ${value ? 'bg-white border-gray-300' : 'bg-gray-50 border-gray-300'
          } hover:border-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-800 transition-colors`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {value || placeholder || 'Select column...'}
        </span>
        <FiChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded  shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search columns..."
                className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded  focus:outline-none focus:ring-1 focus:ring-blue-800"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-100 text-gray-500"
            >
              -- Clear Selection --
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-500 text-center">
                No columns found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-xs text-left hover:bg-blue-50 ${value === option ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-700'
                    }`}
                >
                  {option}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DesignBulkUpload: React.FC = () => {
  const { confirm } = useConfirm();
  const { hasPermission } = useAuth();
  // Project Sites needs DesignSite.view_all; an importer without it goes to My Sites.
  const listPath = hasPermission(DESIGN_RESOURCE, DESIGN_ACTIONS.VIEW_ALL) ? '/designing/sites' : '/designing/my-sites';
  const [topTab, setTopTab] = useState<'upload' | 'history'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [processResult, setProcessResult] = useState<ProcessResult | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<'errors' | 'suggestions' | 'sample' | 'mapping'>('mapping');
  const [searchMapping, setSearchMapping] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // History state
  const [historyData, setHistoryData] = useState<UploadHistoryItem[]>([]);
  const [historyPagination, setHistoryPagination] = useState<HistoryPagination>({ total: 0, page: 1, limit: 10, total_pages: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const params: any = { page, limit: 10 };
      if (historyStatusFilter) params.status = historyStatusFilter;
      const response = await axiosInstance.get('/designing/sites/bulk-history', { params });
      const body = response.data.data;
      setHistoryData(body.data || []);
      const pg = body.pagination || {};
      setHistoryPagination({
        total: Number(pg.total ?? 0),
        page: Number(pg.current_page ?? page),
        limit: Number(pg.per_page ?? 10),
        total_pages: Number(pg.total_pages ?? 0),
      });
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStatusFilter]);

  useEffect(() => {
    if (topTab === 'history') {
      fetchHistory(1);
    }
  }, [topTab, fetchHistory]);

  const handleDeleteHistory = async (item: UploadHistoryItem) => {
    const ok = await confirm({
      title: 'Delete upload',
      message: `Delete upload "${item.file_name}"?`,
      warning: 'The file is removed from storage as well — this cannot be undone.',
      confirmText: 'Delete upload',
      variant: 'danger',
    });
    if (!ok) return;
    setDeletingId(item.id);
    try {
      await axiosInstance.delete(`/designing/sites/bulk-history/${item.id}`);
      toast.success('Upload record deleted successfully');
      fetchHistory(historyPagination.page);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete upload record');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReprocess = async (item: UploadHistoryItem) => {
    setReprocessingId(item.id);
    try {
      await axiosInstance.post('/designing/sites/process', {
        upload_id: item.id,
        mapping: item.field_mapping || {},
      });
      toast.success('Reprocessing started successfully');
      fetchHistory(historyPagination.page);
    } catch (error) {
      console.error('Reprocess failed:', error);
      toast.error('Failed to reprocess upload');
    } finally {
      setReprocessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      success: 'bg-green-100 text-green-800 border-green-200',
      partial: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      pending: 'bg-gray-100 text-gray-800 border-gray-200',
      uploaded: 'bg-gray-100 text-gray-800 border-gray-200',
      analyzing: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      analyzed: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    };
    return styles[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const canReprocess = (status: string) => {
    return ['failed', 'partial', 'analyzed', 'uploaded', 'pending'].includes(status);
  };

  const toggleRow = (rowNumber: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowNumber]: !prev[rowNumber]
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error('Please upload a valid CSV or Excel file');
        return;
      }

      setSelectedFile(file);
      setAnalysisResult(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
        toast.error('Please upload a valid CSV or Excel file');
        return;
      }

      setSelectedFile(file);
      setAnalysisResult(null);
    }
  };

  const handleUploadAndAnalyze = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axiosInstance.post('/designing/sites/upload', formData);
      const result: AnalysisResult = response.data.data;

      setAnalysisResult(result);
      setFieldMapping(result.auto_mapped_fields || {});
      setActiveTab('mapping');
    } catch (error: any) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleProcessImport = async () => {
    if (!analysisResult) return;

    setIsProcessing(true);

    try {
      const response = await axiosInstance.post('/designing/sites/process', {
        upload_id: analysisResult.upload_id,
        mapping: fieldMapping
      });

      const result: ProcessResult = response.data.data;
      setProcessResult(result);

      if (result.status === 'failed' || result.failed_count > 0) {
        setActiveTab('errors');
      } else {
        toast.success(`Successfully imported ${result.success_count} sites`);
        navigate(listPath);
      }
    } catch (error: any) {
      console.error('Process failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setAnalysisResult(null);
    setProcessResult(null);
    setFieldMapping({});
    setSearchMapping('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadSampleFile = () => {
    const sampleData = [
      {
        site_code: "DSN-0001",
        project_short_name: "ESPL-IOS",
        reference_id: "REF-1001",
        site_name: "ESPL iOS Tower - Andheri East",
        activity_name: "Design",
        client_name: "ACME",
        report_type: "New",
        billable: "Yes",
        report_instruction1: "Use the latest report template",
        report_instruction2: "Include the structural drawings",
        priority: "medium",
        required_date: "2026-10-31",
        po_number: "PO-5001",
        po_date: "2026-10-01"
      }
    ];

    const headers = Object.keys(sampleData[0]);

    const csvContent = [
      headers.join(","),
      ...sampleData.map(row =>
        headers.map(header => {
          const value = row[header as keyof typeof row];
          return typeof value === "string" ? `"${value}"` : value;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "designing_bulk_site_upload_sample.csv";
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const filteredRequiredFields = analysisResult?.required_fields.filter(field =>
    field.field_name.toLowerCase().includes(searchMapping.toLowerCase()) ||
    field.display_name.toLowerCase().includes(searchMapping.toLowerCase())
  ) || [];

  const getMappingStats = () => {
    if (!analysisResult) return { mapped: 0, total: 0, required: 0, totalRequired: 0 };

    const requiredFields = analysisResult.required_fields.filter(f => f.required);
    const mappedRequired = requiredFields.filter(f => fieldMapping[f.field_name]).length;
    const totalMapped = Object.values(fieldMapping).filter(v => v).length;

    return {
      mapped: totalMapped,
      total: analysisResult.required_fields.length,
      required: mappedRequired,
      totalRequired: requiredFields.length
    };
  };

  const stats = getMappingStats();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to={listPath} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium mb-3">
            <FiArrowLeft className="w-4 h-4" /> Back to Sites
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Designing Site Upload</h1>
          <p className="text-xs text-gray-600 mt-1">Upload and import multiple Designing sites from Excel or CSV files</p>
        </div>

        {/* Top-level Tabs */}
        <div className="flex space-x-1 mb-6 bg-white rounded shadow-sm border border-gray-200 p-1.5">
          <button
            onClick={() => setTopTab('upload')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium rounded transition-colors ${topTab === 'upload'
              ? 'bg-blue-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            <FiUpload className="text-base" />
            <span>Upload</span>
          </button>
          <button
            onClick={() => setTopTab('history')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium rounded transition-colors ${topTab === 'history'
              ? 'bg-blue-800 text-white'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            <FiClock className="text-base" />
            <span>History</span>
          </button>
        </div>

        {/* History Tab Content */}
        {topTab === 'history' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h3 className="text-sm font-semibold text-gray-900">Upload History</h3>
                <div className="flex items-center space-x-3">
                  <select
                    value={historyStatusFilter}
                    onChange={(e) => setHistoryStatusFilter(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-800"
                  >
                    <option value="">All Statuses</option>
                    <option value="uploaded">Uploaded</option>
                    <option value="analyzed">Analyzed</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="partial">Partial</option>
                    <option value="failed">Failed</option>
                  </select>
                  <button
                    onClick={() => fetchHistory(1)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded transition-colors flex items-center space-x-1"
                  >
                    <FiRefreshCw className={historyLoading ? 'animate-spin' : ''} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>

            {/* History Table */}
            <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
              {historyLoading ? (
                <div className="flex items-center justify-center py-16">
                  <FiRefreshCw className="animate-spin text-blue-800 text-2xl mr-3" />
                  <span className="text-sm text-gray-600">Loading history...</span>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-16">
                  <FiClock className="mx-auto text-4xl text-gray-300 mb-3" />
                  <p className="text-sm text-gray-600">No upload history found</p>
                  <p className="text-xs text-gray-400 mt-1">Uploaded files will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rows</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historyData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <FiFile className="text-blue-800 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">{item.file_name}</p>
                                <p className="text-xs text-gray-400">{(item.file_size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadge(item.status)}`}>
                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-gray-900">
                              <span className="font-medium">{item.total_rows}</span> total
                            </div>
                            {(item.valid_rows !== undefined || item.invalid_rows !== undefined) && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {item.processed_rows !== undefined && <span className="text-blue-600">{item.processed_rows} processed</span>}
                                {item.invalid_rows !== undefined && item.invalid_rows > 0 && (
                                  <span className="text-red-600 ml-1">· {item.invalid_rows} failed</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-900">
                              {item.uploader ? `${item.uploader.first_name} ${item.uploader.last_name}` : '-'}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-900">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {item.created_at ? new Date(item.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                target='_blank'
                                to={item.file_path}
                                className="p-1.5 text-gray-500 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                title="Download file"
                              >
                                <FiDownload className="text-sm" />
                              </Link>
                              {canReprocess(item.status) && (
                                <button
                                  onClick={() => handleReprocess(item)}
                                  disabled={reprocessingId === item.id}
                                  className="p-1.5 text-gray-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                                  title="Process again"
                                >
                                  {reprocessingId === item.id ? (
                                    <FiRefreshCw className="text-sm animate-spin" />
                                  ) : (
                                    <FiPlay className="text-sm" />
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteHistory(item)}
                                disabled={deletingId === item.id}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                title="Delete upload"
                              >
                                {deletingId === item.id ? (
                                  <FiRefreshCw className="text-sm animate-spin" />
                                ) : (
                                  <FiTrash2 className="text-sm" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {historyPagination.total_pages > 1 && (
                <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                  <p className="text-xs text-gray-600">
                    Showing {((historyPagination.page - 1) * historyPagination.limit) + 1} to{' '}
                    {Math.min(historyPagination.page * historyPagination.limit, historyPagination.total)} of{' '}
                    {historyPagination.total} results
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => fetchHistory(historyPagination.page - 1)}
                      disabled={historyPagination.page <= 1}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiChevronLeft className="text-sm" />
                    </button>
                    <span className="text-xs text-gray-700 font-medium">
                      {historyPagination.page} / {historyPagination.total_pages}
                    </span>
                    <button
                      onClick={() => fetchHistory(historyPagination.page + 1)}
                      disabled={historyPagination.page >= historyPagination.total_pages}
                      className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiChevronRight className="text-sm" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Tab Content */}
        {topTab === 'upload' && <>
          {/* Sample File Download Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded border border-blue-200 p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <FiInfo className="text-blue-800 text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Need a template?</h3>
                  <p className="text-xs text-gray-700 mb-3">
                    Download our sample CSV file with all required columns and example data to get started quickly.
                  </p>
                  <button
                    onClick={downloadSampleFile}
                    className="bg-blue-800 text-white px-4 py-2 rounded text-xs font-medium hover:bg-blue-900 transition-colors flex items-center space-x-2"
                  >
                    <FiDownload />
                    <span>Download Sample File</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          {!analysisResult && (
            <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-6">
              <div className="space-y-5">
                {/* File Upload */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Upload File <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-all ${isDragging
                        ? 'border-blue-800 bg-blue-100'
                        : 'border-gray-300 hover:border-blue-800 hover:bg-blue-50'
                        }`}
                    >
                      <FiUpload className="mx-auto text-3xl text-gray-400 mb-3" />
                      <p className="text-sm text-gray-600 mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">CSV, XLS, XLSX (Max 50MB)</p>
                    </div>
                  </div>

                  {selectedFile && (
                    <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded p-3">
                      <div className="flex items-center space-x-3">
                        <FiFile className="text-blue-800 text-lg" />
                        <div>
                          <p className="text-xs font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <FiX className="text-lg" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <button
                  onClick={handleUploadAndAnalyze}
                  disabled={!selectedFile || isUploading}
                  className="w-full bg-blue-800 text-white py-2.5 px-4 rounded text-sm font-medium hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isUploading ? (
                    <>
                      <FiRefreshCw className="animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <FiUpload />
                      <span>Upload & Analyze</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {analysisResult && (
            <div className="space-y-4">
              {/* Process Result Summary - Show after processing */}
              {processResult && (
                <div className={`rounded border p-4 ${processResult.failed_count === 0
                  ? 'bg-green-50 border-green-200'
                  : processResult.success_count > 0
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-red-50 border-red-200'
                  }`}>
                  <div className="flex items-start space-x-3">
                    {processResult.failed_count === 0 ? (
                      <FiCheckCircle className="text-green-600 text-2xl mt-0.5 flex-shrink-0" />
                    ) : (
                      <FiAlertCircle className={`text-2xl mt-0.5 flex-shrink-0 ${processResult.success_count > 0 ? 'text-yellow-600' : 'text-red-600'
                        }`} />
                    )}
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        {processResult.failed_count === 0
                          ? 'Import Completed Successfully!'
                          : processResult.success_count > 0
                            ? 'Import Partially Completed'
                            : 'Import Failed'}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                        <div className="bg-white rounded p-2 border border-gray-200">
                          <p className="text-xs text-gray-600">Total Rows</p>
                          <p className="text-lg font-bold text-gray-900">{analysisResult.total_rows}</p>
                        </div>
                        <div className="bg-white rounded p-2 border border-green-200">
                          <p className="text-xs text-gray-600">Successful</p>
                          <p className="text-lg font-bold text-green-600">{processResult.success_count}</p>
                        </div>
                        <div className="bg-white rounded p-2 border border-red-200">
                          <p className="text-xs text-gray-600">Failed</p>
                          <p className="text-lg font-bold text-red-600">{processResult.failed_count}</p>
                        </div>
                        <div className="bg-white rounded p-2 border border-blue-200">
                          <p className="text-xs text-gray-600">Processed</p>
                          <p className="text-lg font-bold text-blue-800">{processResult.processed_rows}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={resetForm}
                          className="bg-blue-800 text-white px-4 py-2 rounded text-xs font-medium hover:bg-blue-900 transition-colors"
                        >
                          Upload Another File
                        </button>
                        {processResult.failed_count > 0 && (
                          <p className="text-xs text-gray-700">
                            View failed rows in the Validation Errors tab below
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600">Total Rows</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {analysisResult.total_rows}
                      </p>
                    </div>
                    <FiFile className="text-2xl text-blue-800" />
                  </div>
                </div>

                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600">Valid Rows</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        {analysisResult.valid_rows}
                      </p>
                    </div>
                    <FiCheckCircle className="text-2xl text-green-600" />
                  </div>
                </div>

                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600">Invalid Rows</p>
                      <p className="text-2xl font-bold text-red-600 mt-1">
                        {analysisResult.invalid_rows}
                      </p>
                    </div>
                    <FiAlertCircle className="text-2xl text-red-600" />
                  </div>
                </div>

                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600">Fields Mapped</p>
                      <p className="text-2xl font-bold text-blue-800 mt-1">
                        {stats.mapped}/{stats.total}
                      </p>
                    </div>
                    <FiDownload className="text-2xl text-blue-800" />
                  </div>
                </div>
              </div>

              {/* File Info */}
              <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FiFile className="text-blue-800 text-xl" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{analysisResult.file_name}</p>
                      <p className="text-xs text-gray-500">Upload ID: {analysisResult.upload_id}</p>
                    </div>
                  </div>
                  <button
                    onClick={resetForm}
                    className="text-xs text-gray-600 hover:text-red-600 transition-colors flex items-center space-x-1"
                  >
                    <FiX />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                <div className="border-b border-gray-200">
                  <div className="flex space-x-1 p-2 overflow-x-auto">
                    <button
                      onClick={() => setActiveTab('mapping')}
                      className={`px-4 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${activeTab === 'mapping'
                        ? 'bg-blue-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      Field Mapping ({stats.mapped}/{stats.total})
                    </button>
                    <button
                      onClick={() => setActiveTab('errors')}
                      className={`px-4 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${activeTab === 'errors'
                        ? 'bg-blue-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      Validation Errors ({analysisResult.validation_errors.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('suggestions')}
                      className={`px-4 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${activeTab === 'suggestions'
                        ? 'bg-blue-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      Suggestions ({analysisResult.suggestions.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('sample')}
                      className={`px-4 py-2 text-xs font-medium rounded transition-colors whitespace-nowrap ${activeTab === 'sample'
                        ? 'bg-blue-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      Preview Data
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  {/* Field Mapping Tab */}
                  {activeTab === 'mapping' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Map Your Columns</h3>
                          <p className="text-xs text-gray-600 mt-1">
                            Map columns from your file to the required database fields
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-600">
                            Required Fields: <span className="font-semibold text-blue-800">{stats.required}/{stats.totalRequired}</span>
                          </p>
                        </div>
                      </div>

                      {/* Search */}
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={searchMapping}
                          onChange={(e) => setSearchMapping(e.target.value)}
                          placeholder="Search fields..."
                          className="w-full pl-10 pr-3 py-2 text-xs border border-gray-300 rounded  focus:outline-none focus:ring-2 focus:ring-blue-800"
                        />
                      </div>

                      {/* Mapping Grid */}
                      <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
                        {filteredRequiredFields.map((field) => (
                          <div
                            key={field.field_name}
                            className={`border rounded p-3 ${field.required ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <label className="text-xs font-semibold text-gray-900">
                                    {field.display_name}
                                  </label>
                                  {field.required && (
                                    <span className="text-xs text-red-600 font-medium">*Required</span>
                                  )}
                                  <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-300">
                                    {field.type}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mb-1">{field.description}</p>
                                {field.example && (
                                  <p className="text-xs text-gray-500">
                                    <span className="font-medium">Example:</span> {field.example}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="mt-2">
                              <CustomSelect
                                value={fieldMapping[field.field_name] || ''}
                                onChange={(value) =>
                                  setFieldMapping({ ...fieldMapping, [field.field_name]: value })
                                }
                                options={analysisResult.available_columns}
                                placeholder="Select column from file..."
                                required={field.required}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Errors Tab */}
                  {activeTab === 'errors' && (
                    <div className="space-y-3">
                      {/* Show Process Result Errors if available */}
                      {processResult && processResult.failed_rows.length > 0 ? (
                        <div className="space-y-4">
                          <div className="bg-red-50 border border-red-200 rounded p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <FiAlertCircle className="text-red-600 text-lg" />
                              <h4 className="text-sm font-semibold text-red-900">
                                Import Failed Rows ({processResult.failed_count})
                              </h4>
                            </div>
                            <p className="text-xs text-red-700">
                              The following rows failed during import. Review the errors and fix the data.
                            </p>
                          </div>

                          <div className="max-h-96 overflow-y-auto space-y-3">
                            {processResult.failed_rows.map((failedRow, idx) => {
                              const isExpanded = expandedRows[failedRow.row];
                              return (
                                <div
                                  key={idx}
                                  className="bg-white border border-red-300 rounded overflow-hidden"
                                >
                                  <div
                                    className="bg-red-100 border-b border-red-300 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-red-200 transition-colors"
                                    onClick={() => toggleRow(failedRow.row)}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <FiAlertCircle className="text-red-600 text-sm" />
                                      <span className="text-xs font-semibold text-red-900">
                                        Row {Number(failedRow.row) - 1}
                                      </span>
                                      <button
                                        className="p-1 hover:bg-red-300 rounded transition-colors focus:outline-none"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRow(failedRow.row);
                                        }}
                                      >
                                        {isExpanded ? (
                                          <FiChevronUp className="text-red-600 text-sm" />
                                        ) : (
                                          <FiChevronDown className="text-red-600 text-sm" />
                                        )}
                                      </button>
                                    </div>
                                    <span className="text-xs font-medium text-red-700 bg-red-200 px-2 py-1 rounded">
                                      {failedRow.message}
                                    </span>
                                  </div>
                                  {isExpanded && failedRow.data && (
                                    <div className="p-4">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {Object.entries(failedRow.data)
                                          .filter(([key]) => key !== '__parsed_extra')
                                          .map(([key, value]) => (
                                            <div key={key} className="border-b border-gray-200 pb-2">
                                              <p className="text-xs font-medium text-gray-700 mb-1">
                                                {key.replace(/_/g, ' ').toUpperCase()}
                                              </p>
                                              <p className="text-xs text-gray-900 break-all">
                                                {value !== null && value !== undefined && value !== ''
                                                  ? String(value)
                                                  : <span className="text-gray-400 italic">empty</span>
                                                }
                                              </p>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : processResult ? (
                        <div className="text-center">
                          <FiCheckCircle className="mx-auto text-4xl text-green-600 mb-2" />
                          <p className="text-sm font-medium text-gray-900">All rows imported successfully!</p>
                          <p className="text-xs text-gray-600 mt-1">No errors found during import.</p>
                        </div>
                      ) : analysisResult.validation_errors.length === 0 ? (
                        <div className="text-center py-8">
                          <FiCheckCircle className="mx-auto text-4xl text-green-600 mb-2" />
                          <p className="text-sm text-gray-600">No validation errors found!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <FiAlertCircle className="text-yellow-600 text-lg" />
                              <h4 className="text-sm font-semibold text-yellow-900">
                                Pre-Import Validation Errors ({analysisResult.validation_errors.length})
                              </h4>
                            </div>
                            <p className="text-xs text-yellow-700">
                              These errors were detected during file analysis. Fix them before importing.
                            </p>
                          </div>

                          <div className="max-h-96 overflow-y-auto space-y-2">
                            {analysisResult.validation_errors.map((error, idx) => (
                              <div
                                key={idx}
                                className="bg-red-50 border border-red-200 rounded p-3"
                              >
                                <div className="flex items-start space-x-2">
                                  <FiAlertCircle className="text-red-600 text-sm mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-red-900">
                                      Row {Number(error.row_number) - 1} - Column: {error.column}
                                    </p>
                                    <p className="text-xs text-red-700 mt-1">{error.message}</p>
                                    <p className="text-xs text-red-600 mt-1">
                                      Type: {error.error_type.replace(/_/g, ' ')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Suggestions Tab */}
                  {activeTab === 'suggestions' && (
                    <div className="space-y-3">
                      {analysisResult.suggestions.length === 0 ? (
                        <div className="text-center py-8">
                          <FiCheckCircle className="mx-auto text-4xl text-green-600 mb-2" />
                          <p className="text-sm text-gray-600">No suggestions. File looks good!</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {analysisResult.suggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              className="bg-yellow-50 border border-yellow-200 rounded p-3 flex items-start space-x-2"
                            >
                              <FiAlertCircle className="text-yellow-600 text-sm mt-0.5 flex-shrink-0" />
                              <p className="text-xs text-yellow-900">
                                {suggestion.replace(/_/g, ' ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {analysisResult.incomplete_data_rows.length > 0 && (
                        <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                          <p className="text-xs font-medium text-blue-900 mb-2">
                            Incomplete Data Rows:
                          </p>
                          <p className="text-xs text-blue-700">
                            Rows{" "}
                            {analysisResult.incomplete_data_rows
                              .slice(0, 10)
                              .map(row => row - 1)
                              .join(', ')}
                            {analysisResult.incomplete_data_rows.length > 10 && '...'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sample Data Tab */}
                  {activeTab === 'sample' && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {analysisResult.available_columns.map((col) => (
                              <th
                                key={col}
                                className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analysisResult.sample_data.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              {analysisResult.available_columns.map((col) => (
                                <td
                                  key={col}
                                  className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap"
                                >
                                  {row[col] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {!processResult && (
                <>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleProcessImport}
                      disabled={isProcessing || analysisResult.valid_rows === 0 || stats.required < stats.totalRequired}
                      className="flex-1 bg-blue-800 text-white py-2.5 px-4 rounded text-sm font-medium hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                    >
                      {isProcessing ? (
                        <>
                          <FiRefreshCw className="animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <FiArrowRight />
                          <span>Process & Import Sites</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={resetForm}
                      className="sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>

                  {/* Import Warning */}
                  {stats.required < stats.totalRequired && (
                    <div className="bg-red-50 border border-red-200 rounded p-4">
                      <div className="flex items-start space-x-3">
                        <FiAlertCircle className="text-red-600 text-lg mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-red-900">
                            Cannot proceed with import
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            Please map all required fields ({stats.required}/{stats.totalRequired} mapped) before importing.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>}
      </div>
    </div>
  );
};

export default DesignBulkUpload;
